import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_IMAGES = [
  { image_url: 'https://images.pexels.com/photos/416160/pexels-photo-416160.jpeg?auto=compress&cs=tinysrgb&w=500', caption: 'Persian Beauty' },
  { image_url: 'https://images.pexels.com/photos/1404819/pexels-photo-1404819.jpeg?auto=compress&cs=tinysrgb&w=500', caption: 'Tabby Explorer' },
  { image_url: 'https://images.pexels.com/photos/2071873/pexels-photo-2071873.jpeg?auto=compress&cs=tinysrgb&w=500', caption: 'Elegant Grey' },
  { image_url: 'https://images.pexels.com/photos/1170986/pexels-photo-1170986.jpeg?auto=compress&cs=tinysrgb&w=500', caption: 'Snow White' },
  { image_url: 'https://images.pexels.com/photos/96938/pexels-photo-96938.jpeg?auto=compress&cs=tinysrgb&w=500', caption: 'Ginger Delight' },
  { image_url: 'https://images.pexels.com/photos/2558605/pexels-photo-2558605.jpeg?auto=compress&cs=tinysrgb&w=500', caption: 'Curious Kitten' },
];

export default function Home() {
  const revealRef = useRef([]);
  const cleanupRef = useRef(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { src, caption }
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionPos, setTransitionPos] = useState({ x: 50, y: 50 });
  const navigate = useNavigate();

  const handleShopNowClick = (e) => {
    e.preventDefault();
    const x = e.clientX;
    const y = e.clientY;
    setTransitionPos({ x, y });
    setIsTransitioning(true);
    e.currentTarget.classList.add('btn-pressed');
    
    setTimeout(() => {
      navigate('/products');
      window.scrollTo(0, 0);
    }, 800); // Wait for the ripple to engulf the screen
  };

  useEffect(() => {
    // Only target elements that haven't been revealed yet
    const els = document.querySelectorAll('.lp-reveal:not(.visible)');

    // Wait one frame so the browser paints the hidden state before observing
    const rafId = requestAnimationFrame(() => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            // Once visible, stop watching — prevents re-triggering on scroll up
            observer.unobserve(e.target);
          }
        });
      }, { threshold: 0.1 });
      els.forEach(el => observer.observe(el));

      // Store observer for cleanup
      cleanupRef.current = () => observer.disconnect();
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [galleryImages]);

  useEffect(() => {
    axios.get(`${API}/landing-gallery`)
      .then(r => { if (r.data.items?.length) setGalleryImages(r.data.items); })
      .catch(() => {}); // silently fall back to defaults
  }, []);

  // Close lightbox on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="landing-noise" data-testid="home-page">

      {/* ── HERO ── */}
      <section className="lp-hero" id="home" data-testid="hero-section">
        <div>
          <div className="lp-hero-badge">🐱 &nbsp; Anantnag's #1 Cat Shop</div>
          <h1 className="lp-hero-title">
            Everything Your<br />
            <em>Cat Deserves</em>
          </h1>
          <p className="lp-hero-sub">
            Premium cats, nutritious food, and stylish accessories — available in wholesale &amp; retail. Visit us at Mattan Chowk, Anantnag.
          </p>
          <div className="lp-hero-btns">
            <a href="/products" onClick={handleShopNowClick} className="lp-btn-primary" data-testid="hero-shop-btn">Shop Now 🛒</a>
            <a href="#location" className="lp-btn-ghost" data-testid="hero-contact-btn">Find Us 📍</a>
          </div>
          <div className="lp-hero-stats">
            <div><div className="lp-stat-num">50+</div><div className="lp-stat-label">Cat Breeds</div></div>
            <div><div className="lp-stat-num">200+</div><div className="lp-stat-label">Products</div></div>
            <div><div className="lp-stat-num">5★</div><div className="lp-stat-label">Rated Shop</div></div>
          </div>
        </div>
        <div className="lp-hero-visual">
          <div className="lp-floating-badge lp-fb1">🛒 Wholesale Available</div>
          <div className="lp-floating-badge lp-fb2">✅ Quality Guaranteed</div>
          <div className="lp-floating-badge lp-fb3">📦 All Breeds</div>
          <div className="lp-hero-card">
            <img
              src="https://images.pexels.com/photos/45201/kitty-cat-kitten-pet-45201.jpeg?auto=compress&cs=tinysrgb&w=600"
              alt="Cute cat kitten"
              className="lp-hero-cat-img"
              loading="eager"
              fetchpriority="high"
            />
            <span className="lp-hero-card-label">Your perfect companion</span>
          </div>
        </div>
      </section>

      {/* ── STRIP ── */}
      <div className="lp-strip">
        <div className="lp-strip-inner">
          {['Persian Cats','Cat Food Wholesale','Accessories','Siamese Cats','Premium Treats','Grooming Tools','Litter Boxes','Collars & Leashes',
            'Persian Cats','Cat Food Wholesale','Accessories','Siamese Cats','Premium Treats','Grooming Tools','Litter Boxes','Collars & Leashes'].map((t,i) => (
            <span key={i} className="lp-strip-item"><span className="lp-strip-dot">✦</span> {t}</span>
          ))}
        </div>
      </div>

      {/* ── CAT GALLERY ── */}
      <section className="lp-section lp-gallery-section" id="gallery" data-testid="gallery-section">
        <div className="lp-eyebrow">Our Adorable Cats</div>
        <h2 className="lp-section-title">Meet Our Furry Friends</h2>
        <p className="lp-section-sub">Adorable, healthy, and well-cared-for cats waiting for their forever homes.</p>
        <div className="lp-gallery-grid">
          {(galleryImages.length > 0 ? galleryImages : FALLBACK_IMAGES).map((img, i) => {
            const caption = img.caption || img.title || '';
            const isClickable = img.is_clickable;
            // Security: only allow internal paths — never navigate to external URLs or JS URIs
            const rawLink = img.link_url || '/products';
            const linkUrl = rawLink.startsWith('/') && !rawLink.toLowerCase().includes('javascript:') ? rawLink : '/products';
            return (
              <div
                key={i}
                className={`lp-gallery-item lp-reveal ${isClickable ? 'lp-gallery-shoplink' : ''}`}
                onClick={() => {
                  if (isClickable) { navigate(linkUrl); }
                  else { setLightbox({ src: img.image_url, caption }); }
                }}
                title={isClickable ? `Shop: ${caption || 'View products'}` : 'Click to view'}
              >
                <img src={img.image_url} alt={caption || 'Cat photo'} className="lp-gallery-img" loading="lazy" decoding="async" />
                <div className="lp-gallery-overlay">
                  {isClickable && <span className="lp-gallery-shop-badge">🛒 Shop Now</span>}
                  {caption && <span className="lp-gallery-caption">{caption}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── CATEGORIES ── */}
      <section className="lp-section" style={{ background: '#FFFDF9' }} id="categories" data-testid="categories-section">
        <div className="lp-eyebrow">What We Offer</div>
        <h2 className="lp-section-title">Shop by Category</h2>
        <p className="lp-section-sub">From precious kittens to premium food and stylish accessories — everything under one roof.</p>
        <div className="lp-cat-grid">
          {[
            { cls:'lp-c1', badge:'Live', icon:'🐱', name:'Live Cats & Kittens', desc:'Healthy, vaccinated Persian, Siamese, Tabby, and mixed-breed cats available for adoption and purchase.', cat:'Live Cats' },
            { cls:'lp-c2', badge:'Wholesale', icon:'🥫', name:'Cat Food', desc:'Premium dry & wet cat food brands, kitten food, adult & senior formulas — available in bulk wholesale.', cat:'Cat Food' },
            { cls:'lp-c3', badge:'New', icon:'🎀', name:'Accessories', desc:'Collars, harnesses, leashes, toys, beds, scratching posts, and everything your cat needs.', cat:'Accessories' },
            { cls:'lp-c4', badge:'Popular', icon:'🧴', name:'Grooming', desc:'Shampoos, brushes, nail clippers, and complete grooming kits for cats of all fur types.', cat:'Grooming' },
            { cls:'lp-c5', badge:'Bulk', icon:'🏠', name:'Litter & Hygiene', desc:'Clumping and non-clumping litter, litter boxes, scoops, and odor neutralizers — wholesale pricing.', cat:'Litter & Hygiene' },
            { cls:'lp-c6', badge:'Vet Approved', icon:'💊', name:'Health & Wellness', desc:'Vitamins, dewormers, flea treatments, and health supplements recommended by veterinarians.', cat:'Health & Wellness' },
          ].map((c,i) => (
            <Link to={`/products?category=${encodeURIComponent(c.cat)}`} key={i} className={`lp-cat-card ${c.cls} lp-reveal`} style={{ textDecoration:'none' }}
              data-testid={`category-${c.cat.toLowerCase().replace(/\s+/g,'-')}`}>
              <span className="lp-cat-badge">{c.badge}</span>
              <span className="lp-cat-icon">{c.icon}</span>
              <div className="lp-cat-name">{c.name}</div>
              <div className="lp-cat-desc">{c.desc}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── WHY US ── */}
      <section className="lp-section lp-why" id="why" data-testid="why-us-section">
        <div className="lp-why-visual">
          <div className="lp-why-box">
            <span className="lp-why-emoji">😺</span>
            <div className="lp-why-quote">"We don't just sell cats — we find them a loving home."</div>
            <div className="lp-why-author">— Pets Valley, Anantnag</div>
          </div>
          <div className="lp-why-small">
            <div className="lp-wsc-num">10+</div>
            <div className="lp-wsc-label">Years Serving<br />Anantnag</div>
          </div>
        </div>
        <div>
          <div className="lp-eyebrow">Why Choose Us</div>
          <h2 className="lp-section-title">Kashmir's Most<br />Trusted Cat Shop</h2>
          <p className="lp-section-sub">We are proud to serve cat lovers across Anantnag and South Kashmir with quality products and genuine care.</p>
          <div className="lp-features">
            {[
              { icon:'🏷️', title:'Wholesale & Retail Pricing', desc:'Whether you need one bag or a hundred, we offer the best prices for both retail buyers and wholesale dealers.' },
              { icon:'🐾', title:'Healthy & Vaccinated Cats', desc:'All our cats are health-checked, dewormed, and well-cared for before they join your family.' },
              { icon:'📦', title:'Wide Product Range', desc:'Over 200+ cat products including food, accessories, grooming, and health items all stocked locally.' },
              { icon:'💬', title:'Expert Cat Guidance', desc:'Our team helps you pick the right breed, food, and accessories based on your lifestyle and needs.' },
            ].map((f,i) => (
              <div key={i} className="lp-feat-item lp-reveal">
                <div className="lp-feat-icon">{f.icon}</div>
                <div>
                  <div className="lp-feat-title">{f.title}</div>
                  <div className="lp-feat-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRODUCTS ── */}
      <section className="lp-section" style={{ background: '#F5E8D8' }} id="products" data-testid="featured-section">
        <div className="lp-prod-header">
          <div>
            <div className="lp-eyebrow">Our Products</div>
            <h2 className="lp-section-title" style={{ marginBottom: 0 }}>Featured Items</h2>
          </div>
          <Link to="/products" className="lp-btn-ghost" style={{ whiteSpace:'nowrap' }} data-testid="view-all-products">View All →</Link>
        </div>
        <div className="lp-prod-grid">
          {[
            { cls:'lp-p1', tag:'Best Seller', emoji:'🐱', name:'Persian Kitten', type:'Live Cat · Available Now', price:'₹4,000', unit:'onwards' },
            { cls:'lp-p2', tag:'Wholesale', emoji:'🥫', name:'Premium Dry Cat Food', type:'Food · 1kg – 20kg bags', price:'₹450', unit:'per kg' },
            { cls:'lp-p3', tag:'New Arrival', emoji:'🎀', name:'Collar & Bell Set', type:'Accessory · Multiple Colors', price:'₹150', unit:'per piece' },
            { cls:'lp-p4', tag:'Popular', emoji:'🛏️', name:'Cozy Cat Bed', type:'Accessory · Soft & Washable', price:'₹800', unit:'per piece' },
          ].map((p,i) => (
            <Link to="/products" key={i} className="lp-prod-card lp-reveal" data-testid={`featured-product-${i}`}>
              <div className={`lp-prod-img ${p.cls}`}>
                <span className="lp-prod-tag">{p.tag}</span>
                {p.emoji}
              </div>
              <div className="lp-prod-body">
                <div className="lp-prod-name">{p.name}</div>
                <div className="lp-prod-type">{p.type}</div>
                <div className="lp-prod-price">{p.price} <span>{p.unit}</span></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── WHOLESALE ── */}
      <div className="lp-wholesale" data-testid="wholesale-section">
        <div>
          <div className="lp-ws-eyebrow">Bulk Orders Welcome</div>
          <h3 className="lp-ws-title">Wholesale Dealer?<br />We've Got You Covered.</h3>
          <p className="lp-ws-sub">We supply cat food, accessories, and grooming products to pet shops, dealers, and resellers across Anantnag and South Kashmir at unbeatable wholesale rates.</p>
          <div className="lp-ws-perks">
            <div className="lp-ws-perk"><div className="lp-ws-perk-num">30%</div><div className="lp-ws-perk-label">Dealer Discount</div></div>
            <div className="lp-ws-perk"><div className="lp-ws-perk-num">Fast</div><div className="lp-ws-perk-label">Local Delivery</div></div>
            <div className="lp-ws-perk"><div className="lp-ws-perk-num">100+</div><div className="lp-ws-perk-label">SKUs Available</div></div>
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <Link to="/contact" className="lp-btn-primary" style={{ fontSize:'1rem', padding:'16px 36px' }}>Inquire Wholesale →</Link>
        </div>
      </div>

      {/* ── LOCATION ── */}
      <section className="lp-section" style={{ background: '#FFFDF9' }} id="location" data-testid="location-section">
        <div className="lp-eyebrow">Find Us</div>
        <h2 className="lp-section-title">Visit Our Shop</h2>
        <p className="lp-section-sub">Located in the heart of Anantnag at Mattan Chowk — easy to find, always stocked.</p>
        <div className="lp-loc-grid">
          <div>
            <div className="lp-map-box">
              <span className="lp-map-pin">📍</span>
              <div style={{ fontWeight: 600, color: '#1C1209', fontSize: '1rem' }}>Mattan Chowk, Anantnag</div>
              <div>Jammu &amp; Kashmir, India</div>
              <a href="https://www.google.com/maps/place/Pet's+Valley+Anantnag/@33.7317067,75.1478353,724m/data=!3m2!1e3!4b1!4m6!3m5!1s0x38e205005cd02303:0x57ecfa9f65c9267!8m2!3d33.7317068!4d75.1527062!16s%2Fg%2F11vwgmbwr9?entry=ttu&g_ep=EgoyMDI2MDQwNS4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noreferrer" className="lp-btn-primary" style={{ marginTop: 8, fontSize: '0.85rem', padding: '10px 24px' }}>Open in Google Maps</a>
            </div>
            <div className="lp-address-box">
              <strong>📍 Pets Valley</strong><br />
              Mattan Chowk, Anantnag<br />
              Jammu &amp; Kashmir — 192101
            </div>
          </div>
          <div className="lp-info-list">
            {[
              { icon:'🕐', label:'Shop Hours', val:<>Monday – Saturday: 9:00 AM – 8:00 PM<br />Sunday: 10:00 AM – 6:00 PM</> },
              { icon:'📞', label:'Phone / WhatsApp', val:<><a href="tel:+919541399210" style={{ color:'#C45E2A', textDecoration:'none', fontWeight:500 }}>+91 95413 99210</a><br />WhatsApp available for enquiries</> },
              { icon:'📍', label:'Address', val:<>Mattan Chowk, Anantnag<br />Jammu &amp; Kashmir, India</> },
              { icon:'🚗', label:'How to Reach', val:'Located at the main Mattan Chowk junction, easily accessible by road from Anantnag city centre.' },
              { icon:'📦', label:'Wholesale Enquiries', val:'Call or visit in person for bulk pricing. Minimum order quantities apply for wholesale deals.' },
            ].map((r,i) => (
              <div key={i} className="lp-info-row lp-reveal">
                <span className="lp-info-icon">{r.icon}</span>
                <div>
                  <div className="lp-info-label">{r.label}</div>
                  <div className="lp-info-val">{r.val}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="lp-cta" data-testid="final-cta">
        <h2 className="lp-cta-title">Ready to Find Your<br />Perfect Feline Friend?</h2>
        <p className="lp-cta-sub">Visit Pets Valley at Mattan Chowk, Anantnag — or call us for enquiries and wholesale orders.</p>
        <div className="lp-cta-btns">
          <a href="tel:+919541399210" className="lp-cta-btn-w">📞 Call Us Now</a>
          <a href="https://wa.me/919541399210" className="lp-cta-btn-o">💬 WhatsApp Us</a>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="lp-footer" data-testid="footer">
        <div className="lp-footer-top">
          <div>
            <div className="lp-footer-brand">Pets <span>Valley</span> 🐾</div>
            <p className="lp-footer-tagline">Anantnag's premier cat shop — offering premium cats, food, and accessories in wholesale and retail since years. Serving South Kashmir with love and quality.</p>
          </div>
          <div>
            <div className="lp-footer-col-title">Shop</div>
            <ul className="lp-footer-links">
              <li><Link to="/products?category=Live+Cats">Live Cats</Link></li>
              <li><Link to="/products?category=Cat+Food">Cat Food</Link></li>
              <li><Link to="/products?category=Accessories">Accessories</Link></li>
              <li><Link to="/products?category=Grooming">Grooming</Link></li>
              <li><Link to="/products?category=Health+%26+Wellness">Health Products</Link></li>
            </ul>
          </div>
          <div>
            <div className="lp-footer-col-title">Contact</div>
            <ul className="lp-footer-links">
              <li><a href="#location">📍 Mattan Chowk, Anantnag</a></li>
              <li><a href="tel:+919541399210">📞 +91 95413 99210</a></li>
              <li><a href="https://wa.me/919541399210">💬 WhatsApp</a></li>
              <li><Link to="/contact">Wholesale Enquiries</Link></li>
            </ul>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span className="lp-footer-copy">© {new Date().getFullYear()} Pets Valley, Mattan Chowk, Anantnag. All rights reserved.</span>
          <span className="lp-footer-dev">
            Designed & Developed by <strong>Safeer Ahmad Mir</strong> &nbsp;|&nbsp; 📞 <a href="tel:+916005889752">6005889752</a>
          </span>
        </div>
      </footer>

      {/* ── LIGHTBOX ── */}
      {lightbox && (
        <div className="lp-lightbox-overlay" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <button className="lp-lightbox-close" onClick={() => setLightbox(null)} aria-label="Close">✕</button>
          <div className="lp-lightbox-content" onClick={e => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.caption || 'Cat photo'} className="lp-lightbox-img" />
            {lightbox.caption && <div className="lp-lightbox-caption">{lightbox.caption}</div>}
          </div>
        </div>
      )}

      {/* ── ULTRA REALISTIC PAGE TRANSITION ── */}
      {isTransitioning && (
        <>
          <div 
            className="page-transition-overlay"
            style={{ 
              '--tx': `${transitionPos.x}px`, 
              '--ty': `${transitionPos.y}px` 
            }}
          />
          <div className="paw-burst-container">
            <div className="paw-burst">🐾</div>
          </div>
        </>
      )}
    </div>
  );
}
